import React, {
  CSSProperties,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";

import cn from "classnames";
import { IntlShape, useIntl } from "react-intl";

import { UserRef } from "@mapmycustomers/shared/types/User";
import { Collapse, CollapsePanel } from "@mapmycustomers/ui";

import { formatRawDate, userDisplayName } from "@app/util/formatters";

import styles from "./BaseEvent.module.scss";

export interface BaseEventProps {
  children?: ReactNode;
  className?: string;
  date: string;
  expanded: boolean;
  extra?: ReactNode;
  extraClassName?: string;
  onSizeChange?: () => void;
  style?: CSSProperties;
  title: ReactNode;
  user: UserRef | UserRef[];
}

const formatUserNames = (intl: IntlShape, users: UserRef[]) => {
  const moreThan3By = Math.max(0, users.length - 3);
  if (!moreThan3By) {
    return intl.formatList(users.map(userDisplayName), { type: "conjunction" });
  }
  const finalItem = intl.formatMessage(
    {
      id: "dashboard.timeline.event.multipleUsers.suffix",
      defaultMessage: "{count} {count, plural, one {other} other {others}}",
    },
    { count: moreThan3By }
  );
  return intl.formatList([...users.slice(0, 3).map(userDisplayName), finalItem], {
    type: "conjunction",
  });
};

const BaseEvent = forwardRef<HTMLDivElement, BaseEventProps>(
  (
    {
      children,
      className,
      date,
      expanded,
      extra,
      extraClassName,
      onSizeChange,
      style,
      title,
      user,
    },
    ref
  ) => {
    const intl = useIntl();
    const multipleUsers = Array.isArray(user) && user.length > 1;
    const [eventOpen, setEventOpen] = useState(false);
    const isDisabled = children === null;

    const handleChange = useCallback((key: string | string[]) => {
      setEventOpen(!!key.length);
    }, []);

    useEffect(() => {
      setTimeout(() => onSizeChange?.(), 0);
    }, [eventOpen, onSizeChange]);

    useEffect(() => {
      if (!isDisabled) {
        setEventOpen(expanded);
      }
    }, [expanded, isDisabled]);

    return (
      <div className={styles.outerContainer} key={`event-${eventOpen}`} ref={ref} style={style}>
        <div className={styles.tail} />
        <Collapse
          activeKey={eventOpen ? "eventItem" : undefined}
          className={cn(styles.container, styles.eventContainer)}
          onChange={handleChange}
        >
          <CollapsePanel
            collapsible={isDisabled ? "disabled" : "header"}
            extra={formatRawDate(date, "EEEE, MMM d, h:mm a")}
            header={
              <div className={styles.header}>
                <span className={styles.userName}>
                  {multipleUsers
                    ? formatUserNames(intl, user)
                    : userDisplayName(Array.isArray(user) ? user[0] : user)}
                </span>{" "}
                {title}
              </div>
            }
            key="eventItem"
            showArrow={!isDisabled}
          >
            <div className={cn(styles.content, className)}>
              <div className={styles.data}>{children}</div>
              {extra && <div className={cn(styles.extraContainer, extraClassName)}>{extra}</div>}
            </div>
          </CollapsePanel>
        </Collapse>
      </div>
    );
  }
);

export default BaseEvent;
